import { Box, Button, CircularProgress, Collapse, IconButton } from "@mui/material";
import { DataGridPro, type GridPinnedColumnFields, useGridApiRef } from "@mui/x-data-grid-pro";
import { useCallback, useMemo, useState } from "react";
import { WorkPacketsGridFilters } from "./filters/WorkPacketsGridFilters";
import { getWorkPacketGridColumnGrouping, getWorkPacketsGridColumns } from "./helpers/getWorkPacketsGridColumns";
import { CloudDownloadOutlined, FilterList } from "@mui/icons-material";
import { WorkPacketDetailsPopup } from "../WorkPacketDetailsPopup";
import { useWorkPacketsGrid } from "../useWorkPacketsGrid";
import useColumnVisibility from "src/utils/grid/useColumnVisibility";
import { WorkPacketView } from "../WorkPacketView";
import { BulkUpdatePopup } from "./BulkUpdatePopup";
import usePermissions from "components/CustomHooks/usePermissions";
import { useWorkPacketsContext } from "../WorkPacketsContext";
import { WorkPacketType } from "../WorkPacketType";
import { WorkPacketPopupContext } from "./WorkPacketPopupContext";
import { CreateCasePopup } from "src/pages/UserDashboard/Cases/CreateCasePopup";
import { WorkPacketFilter } from "src/types/work-packets.ts";
import { WORK_PACKET_SUPERVISOR_PERMISSIONS } from "src/pages/UserDashboard/WorkPackets/WorkPacketPermission";
import { useCustomWorkPacketRowDisplay } from "../activation-status/useCustomRowDisplay";

const pinnedColumns: GridPinnedColumnFields = {
  right: ["_generateObar", "_details"],
};

interface WorkPacketsGridProps<T extends WorkPacketType> {
  workPacketType: T;
  triggerSummaryRefetch?: () => void;
}

const WorkPacketsGrid = function WorkPacketsGrid<T extends WorkPacketType>({
  workPacketType,
  triggerSummaryRefetch,
}: WorkPacketsGridProps<T>) {
  const { isAdmin, hasPermission } = usePermissions();
  const isSupervisor = hasPermission(WORK_PACKET_SUPERVISOR_PERMISSIONS[workPacketType]);
  const canExportCsv = isAdmin || isSupervisor;

  const apiRef = useGridApiRef();
  const [showFilters, setShowFilters] = useState(true);
  const [currentPacketId, setCurrentPacketId] = useState<string | null>(null);
  const [showBulkUpdatePopup, setShowBulkUpdatePopup] = useState(false);
  const [showCreateCasePopup, setShowCreateCasePopup] = useState(false);

  const { currentFilters, currentView: view } = useWorkPacketsContext();
  const isPreCaseView = !!currentFilters[WorkPacketFilter.PreCase];

  const {
    data,
    paginationModel,
    total,
    loading,
    filters,
    rowSelection,
    callbacks,
    selectAllEnabled,
    excludeWorkPacketIds,
    resetSelection,
    triggerFetchWorkPackets,
    handleExport,
    isExporting,
    localeText,
    effectiveSelectionCount,
  } = useWorkPacketsGrid(workPacketType, triggerSummaryRefetch);

  const allowEditPacketActionAndNotes = isAdmin || isSupervisor || view === WorkPacketView.MyPackets;

  const columns = getWorkPacketsGridColumns(workPacketType);
  const computedColumns = useMemo(
    () =>
      isPreCaseView
        ? columns.filter(col => col.field !== "recoveryStreamServer")
        : columns.filter(
            col =>
              ![
                "remainingOpenBalance",
                "escalationCount",
                "lastChildInvoiceId",
                "deniedReason",
                "abandonedReason",
              ].includes(col.field),
          ),
    [columns, isPreCaseView],
  );

  /**
   * DNA-3343 Setting showDeniedReasonColumn to false as we are removing configCat feature flags and this is
   * marked false by default
   */
  const showDeniedReasonColumn = false;

  const filteredColumns = useMemo(
    () => computedColumns.filter(col => (col.field === "deniedReason" ? showDeniedReasonColumn : true)),
    [computedColumns, showDeniedReasonColumn],
  );

  const columnGroupingModel = getWorkPacketGridColumnGrouping(workPacketType);

  const { columnVisibilityModel, handleColumnVisibilityChange } = useColumnVisibility("work-packets-grid");

  const toggleFilters = () => {
    setShowFilters(prev => !prev);
  };

  const isCellEditable = useCallback(() => allowEditPacketActionAndNotes, [allowEditPacketActionAndNotes]);

  const { slots } = useCustomWorkPacketRowDisplay();

  const onBulkActionsClick = async () => {
    setShowBulkUpdatePopup(true);
  };

  return (
    <>
      <Box bgcolor="#fff" p={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" gap="12px">
            <Button
              startIcon={<FilterList />}
              variant="outlined"
              onClick={toggleFilters}
              className={showFilters ? "" : "active"}
            >
              Filters
            </Button>
          </Box>

          <Box display="flex" gap="12px">
            {[WorkPacketType.SHORTAGES, WorkPacketType.CHARGEBACKS].includes(workPacketType) && isPreCaseView && (
              <Button
                variant="outlined"
                disabled={rowSelection.length === 0}
                onClick={() => setShowCreateCasePopup(true)}
              >
                Attach a Case ID
              </Button>
            )}
            <Button variant="outlined" disabled={rowSelection.length === 0} onClick={onBulkActionsClick}>
              Bulk Actions
            </Button>
            {canExportCsv && (
              <IconButton className="outlined" onClick={handleExport} disabled={loading || isExporting}>
                <CloudDownloadOutlined />

                {isExporting && <CircularProgress size={14} style={{ marginLeft: "12px", color: "#101828" }} />}
              </IconButton>
            )}
          </Box>
        </Box>

        <Collapse in={showFilters}>
          <WorkPacketsGridFilters loading={loading} />
        </Collapse>

        <Box position="relative" overflow="auto">
          <Box maxHeight="70vh" display="flex" flexDirection="column">
            <WorkPacketPopupContext.Provider value={setCurrentPacketId}>
              <DataGridPro
                {...callbacks}
                apiRef={apiRef}
                rows={data}
                editMode="row"
                isCellEditable={isCellEditable}
                columns={filteredColumns}
                pinnedColumns={pinnedColumns}
                columnGroupingModel={columnGroupingModel}
                getRowId={row => row.packetId}
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                rowCount={total}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                filterMode="server"
                sortingMode="server"
                rowSelectionModel={rowSelection}
                keepNonExistentRowsSelected
                checkboxSelection
                disableRowSelectionOnClick
                loading={loading}
                rowHeight={70}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                localeText={localeText}
                slots={slots}
              />
            </WorkPacketPopupContext.Provider>
          </Box>
        </Box>
      </Box>

      {currentPacketId != null && (
        <WorkPacketDetailsPopup
          workPacketId={currentPacketId}
          open
          onClose={() => setCurrentPacketId(null)}
          onSuccessfulUpdate={() => {
            triggerFetchWorkPackets();
          }}
        />
      )}

      {showBulkUpdatePopup && (
        <BulkUpdatePopup
          open={showBulkUpdatePopup}
          onClose={(success?: boolean) => {
            setShowBulkUpdatePopup(false);

            // Refetch data if the bulk update was successful
            if (success) {
              triggerFetchWorkPackets();
              resetSelection();
            }
          }}
          workPacketIds={rowSelection as string[]}
          filters={filters}
          selectedAll={selectAllEnabled}
          excludeIds={excludeWorkPacketIds}
          allowEditPacketActionAndNotes={allowEditPacketActionAndNotes}
          effectiveSelectionCount={effectiveSelectionCount}
        />
      )}
      {showCreateCasePopup && (
        <CreateCasePopup
          open={showCreateCasePopup}
          excludedWorkPacketsIds={excludeWorkPacketIds}
          filters={filters}
          onClose={(success?: boolean) => {
            setShowCreateCasePopup(false);

            // Refetch data if case was created successfully
            if (success) {
              triggerFetchWorkPackets();
              resetSelection();
            }
          }}
          selectedWorkPacketsIds={selectAllEnabled ? [] : (rowSelection as string[])}
          workPacketType={workPacketType}
        />
      )}
    </>
  );
};

export default WorkPacketsGrid;
